import "./receptionTypes.js";
import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Box, Card, CardContent, Typography } from "@mui/material";

import { status } from "shared/src/appointment.mjs";

import { CheckedInClientHeader } from "./CheckedInClientHeader";
import { CheckedInClient } from "./CheckedInClient";
import { indexBy } from "shared/src/util.mjs";

/**
 * A component to display the checked-in client list
 *
 * @typedef {object} CheckedInClientListProps
 * @property {Array<Appointment>} appointments
 * @property {function} setIsHovered Setter function for the isHovered state
 * @property {string} hoverOverId
 * @property {function} setHoverOverId Setter function for the hoverOverId state
 * @property {Set<string>} nameDayNames
 *
 * @param {CheckedInClientListProps} props
 * @returns {ReactElement}
 */

export const CheckedInClientList = ({
  appointments,
  setIsHovered,
  hoverOverId,
  setHoverOverId,
  nameDayNames,
  setShowDetailModal,
}) => {
  const [checkedInClients, setCheckedInClients] = useState(appointments);
  const { t } = useTranslation();

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const statusPriorities = {
    [status.CONFIRMED]: 1,
    [status.ASSIGNED]: 2,
    [status.WAITING]: 3,
  };
  // Memoize parent-child relationships
  const { parentToChild, childToParent } = useMemo(() => {
    /** @type {Record<string, any>} */
    const p2c = indexBy(appointments, (a) => a.parent_id);
    delete p2c["null"];
    /** @type {Record<string, any>} */
    const c2p = {};
    for (const appt of appointments) {
      if (appt.parent_id != null) {
        c2p[appt.id] =
          appointments.find((a) => a.id === appt.parent_id) || null;
      }
    }
    return { parentToChild: p2c, childToParent: c2p };
  }, [appointments]);

  useEffect(() => {
    const filteredAppointments = appointments.filter((appointment) => {
      // Get child appointment for this parent (if any)
      const childAppointment = parentToChild[appointment.id];

      // If appointment has a child
      if (childAppointment) {
        // Show parent only if child is in TREATED or FINISHED state
        return (
          (appointment.status_id === status.CONFIRMED ||
            appointment.status_id === status.WAITING ||
            appointment.status_id === status.ASSIGNED) &&
          (childAppointment.status_id === status.TREATED ||
            childAppointment.status_id === status.FINISHED)
        );
      }

      // If appointment is a child (has a parent_id)
      if (appointment.parent_id) {
        // Show child if it's in WAITING, CONFIRMED, or ASSIGNED state
        return (
          appointment.status_id === status.CONFIRMED ||
          appointment.status_id === status.WAITING ||
          appointment.status_id === status.ASSIGNED
        );
      }

      // For appointments without parent or child, show if in allowed states
      return (
        appointment.status_id === status.CONFIRMED ||
        appointment.status_id === status.WAITING ||
        appointment.status_id === status.ASSIGNED
      );
    });

    // Sort appointments by status priority
    const sortedAppointments = filteredAppointments.sort(
      (a, b) => statusPriorities[a.status_id] - statusPriorities[b.status_id],
    );

    setCheckedInClients(sortedAppointments);
  }, [appointments, parentToChild]);
  return (
    <Card onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <CardContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h2" gutterBottom>
            {t("reception.checked_in_clients")}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "grid",
            gap: "0 1rem",
            gridAutoFlow: "row",
            gridTemplateColumns: "repeat(3, 1fr)",
          }}
        >
          <CheckedInClientHeader />
          <CheckedInClientHeader />
          <CheckedInClientHeader />
        </Box>

        <Box
          sx={{
            display: "grid",
            gap: "0 1rem",
            gridAutoFlow: "row",
            gridTemplateColumns: "1fr 1fr 1fr",
            gridAutoRows: "1fr",
          }}
        >
          {checkedInClients.map((appointment) => {
            const parentAppointment = childToParent[appointment.id] || null;
            return (
              <CheckedInClient
                key={appointment.id}
                parentAppointment={parentAppointment}
                appointment={appointment}
                hoverOverId={hoverOverId}
                setHoverOverId={setHoverOverId}
                nameDayNames={nameDayNames}
                setShowDetailModal={setShowDetailModal}
              />
            );
          })}
        </Box>
      </CardContent>
    </Card>
  );
};
